import { pipe } from 'fp-ts/function';
import { fromEntries } from 'fp-ts/Record';
import { map } from 'fp-ts/Array';
import provinces from 'provinces';
import { makeEntry } from '~/adapters/typescript/makeEntry';
import { countryList } from './countryList';

export const defaultCountryCode = 'US';

export const countryCodes: string[] = countryList.map((country) => country.code).sort();
export const countryCodesSortedByFullName: string[] = countryList
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((country) => country.code);

// for example 'US' -> 'United States'
export const countryNameByCode: Record<string, string> = pipe(
    countryList,
    map((country) => makeEntry(country.code, country.name)),
    fromEntries,
);

// in some countries EasyPost converts provinces to ISO codes, while in other countries to full names
// that's why we support province dropdown only for US and Canada where Easypost reliable uses ISO codes
// https://customink.monday.com/boards/3548701582/pulses/4511796849/posts/2337803451
const countryCodesSupportingProvinces = ['US', 'CA'];

// Province has nullable 'short', but we are filtering it
type SupportedProvince = Omit<Province, 'short'> & { short: string };

const supportedProvinces = provinces.filter(
    (province) => province.short && countryCodesSupportingProvinces.includes(province.country),
) as SupportedProvince[];

// for example 'US' -> ['FL', 'NY', 'TX', ...]
export const provinceCodesOfCountryCode = (countryCode: string): string[] =>
    supportedProvinces
        .filter((province) => province.country === countryCode)
        .map((province) => province.short)
        .sort();

// for example 'FL' -> 'Florida'
export const provinceNameByCode: Record<string, string> = pipe(
    supportedProvinces,
    map((province) => makeEntry(province.short, province.name)),
    fromEntries,
);
